/**
 * Tournament Event competition configuration (display vs rules).
 *
 * `competition` holds match/participation rules only — not fees, caps, courts, or schedule
 * (those live on Event settings, Tournament Module, or Arena). See TOURNAMENT_EVENT.md.
 *
 * Sport resolves playersPerSide from format via match-type catalog — never stored on Event.
 */

import {
  resolvePlayersPerSideFromMatchFormat,
} from "../sport/sportMatchFormatCatalog";
import { DEFAULT_SPORT } from "../sport/sportTypes";
import { ENTRY_CATEGORIES } from "../competition/entryManagementTypes";
import { normalizeCustomAgeRule } from "./participationAge";
import { resolveLegacyEventCompetition } from "./eventCompetitionLegacy";
import {
  normalizeEventMatchFormat,
  resolveEventMatchFormat,
} from "./eventMatchFormat";

export const EVENT_FORMATS = Object.freeze({
  SINGLES: "singles",
  DOUBLES: "doubles",
});

export const EVENT_FORMAT_VALUES = Object.freeze([
  EVENT_FORMATS.SINGLES,
  EVENT_FORMATS.DOUBLES,
]);

export const PARTICIPATION_GENDER = Object.freeze({
  OPEN: "open",
  MEN: "men",
  WOMEN: "women",
  MIXED: "mixed",
});

export const PARTICIPATION_GENDER_VALUES = Object.freeze([
  PARTICIPATION_GENDER.OPEN,
  PARTICIPATION_GENDER.MEN,
  PARTICIPATION_GENDER.WOMEN,
  PARTICIPATION_GENDER.MIXED,
]);

/** Age band on participation (extensible; rating/club later). */
export const PARTICIPATION_AGE = Object.freeze({
  OPEN: "open",
  U15: "u15",
  U17: "u17",
  U19: "u19",
  ADULT: "adult",
  SENIOR_35: "35+",
  SENIOR_40: "40+",
  SENIOR_45: "45+",
  SENIOR_50: "50+",
  CUSTOM: "custom",
});

export const PARTICIPATION_AGE_VALUES = Object.freeze(Object.values(PARTICIPATION_AGE));

/**
 * @typedef {Object} EventDisplay
 * @property {string} name
 */

/**
 * @typedef {Object} EventParticipation
 * @property {'open'|'men'|'women'|'mixed'} gender
 * @property {string} [age] Band id from PARTICIPATION_AGE (default open)
 * @property {{ label?: string, minAge?: number | null, maxAge?: number | null }} [ageRule] When age is `custom`
 */

/**
 * @typedef {Object} EventCompetition
 * @property {'singles'|'doubles'} format
 * @property {EventParticipation} participation
 * @property {{ setsToWin: number, pointsToWin: number }} [matchFormat]
 */

/**
 * @typedef {Object} TournamentEventDefinition
 * @property {EventDisplay} display
 * @property {EventCompetition} competition
 * @property {string} [eventId]
 * @property {string} [clientKey]
 */

export function isEventFormat(value) {
  return EVENT_FORMAT_VALUES.includes(value);
}

export function isParticipationGender(value) {
  return PARTICIPATION_GENDER_VALUES.includes(value);
}

export function isParticipationAge(value) {
  if (PARTICIPATION_AGE_VALUES.includes(value)) return true;
  if (value === PARTICIPATION_AGE.CUSTOM) return true;
  return false;
}

function normalizeAgeRule(input, ageId) {
  if (ageId !== PARTICIPATION_AGE.CUSTOM) {
    if (!input?.ageRule) return undefined;
    const custom = normalizeCustomAgeRule(input.ageRule);
    return custom.ok ? custom.rule : undefined;
  }
  const custom = normalizeCustomAgeRule(input?.ageRule || input);
  if (!custom.ok) return null;
  return custom.rule;
}

function normalizeParticipation(input) {
  if (!input || typeof input !== "object") return null;
  const gender = String(input.gender || "").trim();
  if (!isParticipationGender(gender)) return null;
  let ageRaw = String(input.age ?? PARTICIPATION_AGE.OPEN).trim() || PARTICIPATION_AGE.OPEN;
  if (ageRaw === PARTICIPATION_AGE.CUSTOM) {
    const ageRule = normalizeAgeRule(input, PARTICIPATION_AGE.CUSTOM);
    if (!ageRule) return null;
    return { gender, age: PARTICIPATION_AGE.CUSTOM, ageRule };
  }
  const age = PARTICIPATION_AGE_VALUES.includes(ageRaw) ? ageRaw : PARTICIPATION_AGE.OPEN;
  const ageRule = normalizeAgeRule(input, age);
  const out = { gender, age };
  if (ageRule) out.ageRule = ageRule;
  return out;
}

/**
 * @param {unknown} input
 * @param {string | null | undefined} [sportId]
 * @returns {EventCompetition | null}
 */
export function normalizeEventCompetition(input, sportId = DEFAULT_SPORT) {
  if (!input || typeof input !== "object") return null;
  const format = String(input.format || "").trim();
  const participation = normalizeParticipation(input.participation || input);
  if (!isEventFormat(format) || !participation) return null;
  return {
    format,
    participation,
    matchFormat: normalizeEventMatchFormat(input.matchFormat, sportId),
  };
}

/**
 * @param {unknown} input
 * @returns {TournamentEventDefinition | null}
 */
export function normalizeEventDefinition(input) {
  if (!input || typeof input !== "object") return null;
  const displayName = String(input.display?.name ?? input.name ?? "").trim();
  const competition = normalizeEventCompetition(input.competition || input);
  if (!displayName || !competition) return null;
  return {
    eventId: input.eventId || undefined,
    clientKey: input.clientKey || undefined,
    display: { name: displayName },
    competition,
  };
}

/**
 * @param {TournamentEventDefinition} def
 * @returns {{ ok: boolean, fieldErrors: Record<string, string>, errors: string[] }}
 */
export function validateEventDefinition(def) {
  const fieldErrors = {};
  const errors = [];
  const name = String(def?.display?.name || "").trim();
  if (!name) {
    fieldErrors["display.name"] = "Event name is required.";
    errors.push(fieldErrors["display.name"]);
  }
  const format = def?.competition?.format;
  if (!isEventFormat(format)) {
    fieldErrors["competition.format"] = "Choose Singles or Doubles.";
    errors.push(fieldErrors["competition.format"]);
  }
  const gender = def?.competition?.participation?.gender;
  if (!isParticipationGender(gender)) {
    fieldErrors["competition.participation.gender"] =
      "Choose participation (Open, Men, Women, or Mixed).";
    errors.push(fieldErrors["competition.participation.gender"]);
  }
  const age = def?.competition?.participation?.age ?? PARTICIPATION_AGE.OPEN;
  if (age === PARTICIPATION_AGE.CUSTOM) {
    const custom = normalizeCustomAgeRule(def?.competition?.participation?.ageRule);
    if (!custom.ok) {
      fieldErrors["competition.participation.ageRule"] = custom.error || "Invalid custom age band.";
      errors.push(fieldErrors["competition.participation.ageRule"]);
    }
  } else if (!PARTICIPATION_AGE_VALUES.includes(age)) {
    fieldErrors["competition.participation.age"] = "Choose an age band or Open.";
    errors.push(fieldErrors["competition.participation.age"]);
  }
  return { ok: errors.length === 0, fieldErrors, errors };
}

/**
 * Entry category derived from competition rules (not display name).
 * @param {EventCompetition} competition
 */
export function entryCategoryFromEventCompetition(competition) {
  const format = competition?.format;
  const gender = competition?.participation?.gender;
  if (format === EVENT_FORMATS.SINGLES) return ENTRY_CATEGORIES.SINGLES;
  if (format === EVENT_FORMATS.DOUBLES && gender === PARTICIPATION_GENDER.MIXED) {
    return ENTRY_CATEGORIES.MIXED_DOUBLES;
  }
  if (format === EVENT_FORMATS.DOUBLES) return ENTRY_CATEGORIES.DOUBLES;
  return null;
}

/**
 * Entry roster size from competition format (delegates to Sport catalog when sportId provided).
 * @param {EventCompetition} competition
 * @param {string} [sportId]
 */
export function expectedMemberCountForEventCompetition(competition, sportId = DEFAULT_SPORT) {
  if (!competition?.format) return null;
  try {
    return resolvePlayersPerSideFromMatchFormat(competition.format, sportId);
  } catch {
    return null;
  }
}

/**
 * @param {'singles'|'doubles'} format
 * @param {string} [sportId]
 */
export function resolvePlayersPerSideFromFormat(format, sportId = DEFAULT_SPORT) {
  return resolvePlayersPerSideFromMatchFormat(format, sportId);
}

/**
 * @param {object | null | undefined} event
 * @returns {EventCompetition | null}
 */
export function resolveEventCompetition(event) {
  const normalized = normalizeEventCompetition(
    event?.competition,
    event?.sportId || DEFAULT_SPORT
  );
  if (normalized) return normalized;
  const name = String(event?.display?.name ?? event?.name ?? "").trim();
  if (!name) return null;
  const legacy = resolveLegacyEventCompetition(name);
  if (!legacy) return null;
  return {
    ...legacy,
    matchFormat: normalizeEventMatchFormat(legacy.matchFormat),
  };
}

export { resolveEventMatchFormat, resolveArenaPlayMatchFormat } from "./eventMatchFormat";

/**
 * @param {object | null | undefined} event
 */
export function resolveEventDisplayName(event) {
  return String(event?.display?.name ?? event?.name ?? "").trim() || "Event";
}
